import React, { FunctionComponent as FC, useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux'; // To call Action Creators
import { bindActionCreators } from 'redux';
import { actionCreators } from '../../../../../state';
import { GrantViewTypes } from '../../../../../types/data';
import * as d3 from  "d3";
import { select, zoom } from 'd3';
import _ from 'lodash';
import { themeColor, visualizationStyle, grantViewStyle } from '../../../../../types/style.global';
import './index.scss';

import SideViewTooltipComponent from '../../SideViewTooltipComponent';
import { TooltipTypes } from '../../../../../types/data';

var numeral = require('numeral');


interface ScatterplotProps {
  chartTitle: string;
  data: GrantViewTypes.ScatterplotDataUnit[];
  width: number;
  height: number;
}

const Scatterplot: React.FC<ScatterplotProps> = (props) => {
    const { chartTitle, data, width, height } = props

    const dispatch = useDispatch()
    const {
      setSelectedGrantTopicList,
    } = bindActionCreators(actionCreators, dispatch)

    const [selectedPoints, setSelectedPoints] = useState<Set<number>>(new Set());
    const [showTooltip, setShowTooltip] = useState<boolean>(false)
    const [tooltipOpacity, setTooltipOpacity] = useState<number>(0)
    const [tooltipPosition, setTooltipPosition] = useState<TooltipTypes.TooltipPosition>({
        left: 0,
        top: 0,
    })
    const [tooltipData, setTooltipData] = useState<TooltipTypes.TooltipDataUnit[]>([{ " ": 0 }])


    const handleScatterplotMouseEnter = (event:  React.MouseEvent<SVGCircleElement>) => {
        // console.log('in handleScatterplotMouseEnter')
        const idString = event.currentTarget.id;
        const [grantField, fundingAmount] = idString.split('|');
        // 方括号内的表达式用于计算属性名
        // setTooltipData([{ [grantField]: "Funding Amount: "+fundingAmount }])
        setTooltipData([{ [grantField]: "Funding Amount: $ " + numeral(fundingAmount).format('0,0') }])
        setTooltipPosition({
            left: event.clientX + 24,
            top: event.clientY + 24,
        })
        setShowTooltip(true)
        setTooltipOpacity(1)
      }
    const handleScatterplotMouseLeave = (event:  React.MouseEvent<SVGCircleElement>) => {
        setShowTooltip(false)
        setTooltipOpacity(0)
      }

    // 点击点的事件处理函数
    const handlePointClick = (index: number) => {
        // console.log('selectedPoints = ', selectedPoints)
        // setSelectedPoints(() =>{
        //     // Set在 JavaScript 中是引用类型，需要new一个新的，否则是在原set上直接修改
        //     const newSelectedPoints = new Set(selectedPoints);
        //     if (newSelectedPoints.has(index)) {
        //         newSelectedPoints.delete(index);
        //     } else {
        //         newSelectedPoints.add(index);
        //     }
        //     return newSelectedPoints;
        // });
        const updatedSelectedPoints = new Set(selectedPoints);

        // 根据点是否已被选中来添加或移除
        updatedSelectedPoints.has(index) ? updatedSelectedPoints.delete(index) : updatedSelectedPoints.add(index);

        // 从updatedSelectedPoints生成SelectedGrantTopicUnit数组并更新状态
        const selectedGrantTopics = Array.from(updatedSelectedPoints).map((pointIndex) => ({
            grantFieldName: sortedData[pointIndex].grantField,
            grantFieldCode: sortedData[pointIndex].grantFieldCode,
        }));
        console.log('selectedGrantTopics = ', selectedGrantTopics)

        setSelectedGrantTopicList(selectedGrantTopics);
        setSelectedPoints(updatedSelectedPoints);
    };

    // 高亮选中点的样式
    // const highlightStyle = {
    //     stroke: 'black',
    //     strokeWidth: 2,
    // };

    // 按照fundingAmount降序排序数据
    const sortedData = [...data].sort((a, b) => b.fundingAmount - a.fundingAmount);

    // 计算x轴和y轴的比例尺
    const xExtent = d3.extent(data, d => d.xValue) as [number, number];
    const yExtent = d3.extent(data, d => d.yValue) as [number, number];

    // 计算范围的边距
    const xMargin = (xExtent[1] - xExtent[0]) * 0.05; // x轴边距为范围的5%
    const yMargin = (yExtent[1] - yExtent[0]) * 0.05; // y轴边距为范围的5%

    // 创建x轴和y轴的比例尺
    const xScale = d3.scaleLinear()
        .domain([xExtent[0] - xMargin, xExtent[1] + xMargin])
        .range([0, width * 0.95]);

    const yScale = d3.scaleLinear()
        .domain([yExtent[0] - yMargin, yExtent[1] + yMargin])
        .range([height * 0.95, 0]);

    // 创建颜色比例尺
    const grantFieldColorMap: { [key: string]: string } = visualizationStyle.Scatterplot.grantFieldColorMap;
    // const colorArrayScatterPlot = [
    //     '#e07a5fff', '#3d405bff', '#5f797bff', '#81b29aff', '#f2cc8fff', // '#f4f1deff',
    //    // '#264653', '#2a9d8f', '#e9c46a', '#f4a261', '#e76f51', '#ec8c74'
    //    // '#ffdedcff', '#f1b5b5ff', '#93939bff', '#aab7aeff', '#c6d2c6ff', '#dfe2dbff'
    // ]
    // const colorScale = d3.scaleOrdinal<string>().range(colorArrayScatterPlot);
    // const colorScale = d3.scaleOrdinal(d3.schemeSet2);

    // 创建点的大小比例尺
    const sizeScale = d3.scalePow()
        .domain(d3.extent(data, d => d.fundingAmount) as [number, number])
        .range([5, 50])
        .exponent(0.4); // 最小和最大半径

    const fontSizeScale = d3.scaleLog()
        .domain([5, 50])
        .range([2, 16]); // 最小和最大半径

    // 定义阈值
    const sizeThresholdOfLable = 20;

    // 标准化paperPerDollar的值用于透明度
    const opacityScale = d3.scaleLinear()
        .domain(d3.extent(data, d => d.paperPerDollar) as [number, number])
        .range([0.1, 1]); // 最小和最大透明度

    // make the svg element zoomable and dragable
    const scatterplotSVGRef = useRef(null as SVGSVGElement| null)
    const scatterplotGroupRef = useRef<SVGGElement>(null)
    useEffect(() => {
        if (scatterplotSVGRef.current && scatterplotGroupRef.current) {
            const svg = select(scatterplotSVGRef.current);
            const group = select(scatterplotGroupRef.current);

            // @ts-ignore
            svg.call(zoom()
                .scaleExtent([0.5, 2]) // Set the zoom range
                .on('zoom', (event) => {
                    group.transition()
                        .duration(80) // Set the zoom speed
                        .attr('transform', event.transform);
                })
            );
        }
    }, []);
    ////////////////////  (3) define global style (end)  ////////////////////

    if (!width || width <= 0) return null;

    return (
        <div className="scatterplot-chart" style={{ width, height: width }}>
            <svg
                width={Math.max(0, width - 2*grantViewStyle.scatterplot.margin)}
                height={Math.max(0, width - 2*grantViewStyle.scatterplot.margin)}
                // width={width-2*grantViewStyle.scatterplot.margin}
                // height={width-2*grantViewStyle.scatterplot.margin}
                viewBox={`0 0 ${width * 1.1} ${height * 1.1}`}
                ref={scatterplotSVGRef}
            >
                <g ref={scatterplotGroupRef}>
                {/* <text x={width / 2} y="20" textAnchor="middle">{chartTitle}</text> */}
                {sortedData.map((d, i) => {
                    const radius = sizeScale(d.fundingAmount);
                    // const fillColor = d3.color(colorScale(d.grantField)); // 获取D3颜色对象
                    // fillColor.opacity = opacityScale(d.paperPerDollar); // 设置填充颜色的透明度
                    let fillColor = d3.color(grantFieldColorMap[d.grantField]) // 获取D3颜色对象
                    let textColor = d3.color(grantFieldColorMap[d.grantField])
                    // let fillColor = d3.color(colorScale(d.grantField)) // 获取D3颜色对象
                    // let textColor = d3.color(colorScale(d.grantField))
                    let strokeColor = _.cloneDeep(fillColor)
                    if (fillColor && strokeColor) { // 检查fillColor是否非null
                        fillColor.opacity = 0.3;
                        strokeColor.opacity = 0.55;
                        // fillColor.opacity = opacityScale(d.paperPerDollar); // 设置填充颜色的透明度
                      }

                    const textParts = d.grantField.split(' '); // 简单地以空格分割文本作为示例

                    return (
                        <g key={i} onClick={() => handlePointClick(i)}>
                        <circle
                            key={i}
                            className='scatterplot-point'
                            id={d.grantField + '|' + d.fundingAmount}
                            cx={xScale(d.xValue)}
                            cy={yScale(d.yValue)}
                            r={radius}
                            fill={fillColor ? fillColor.toString() : themeColor.defaultMode.baseColor}
                            opacity={0.95}
                            // opacity={opacityScale(d.paperPerDollar)}
                            onClick={() => handlePointClick(i)}
                            onMouseEnter={(e) => handleScatterplotMouseEnter(e)}
                            onMouseLeave={(e) => handleScatterplotMouseLeave(e)}
                            stroke={selectedPoints.has(i) ? (strokeColor ? strokeColor.toString() : themeColor.defaultMode.baseColor) : 'none'} // 选中点的描边颜色
                            strokeWidth={selectedPoints.has(i) ? 2.5 : 0} // 选中点的描边宽度
                            strokeOpacity={1} // 保持描边的不透明度为1
                        />
                        {radius > sizeThresholdOfLable && (
                            <text
                              x={xScale(d.xValue)}
                              y={yScale(d.yValue)} // 文本位于圆的上方
                              textAnchor="middle"
                              fontSize={fontSizeScale(radius)}
                              fill={grantFieldColorMap[d.grantField]}
                            //   fill={colorScale(d.grantField)}

                            //   fill={textColor?.toString()}
                              opacity={1}
                            >
                              {d.grantField}
                            </text>
                        )}
                        </g>
                    )
                })}
                </g>
            </svg>
            <SideViewTooltipComponent
                tooltipPosition={tooltipPosition}
                tooltipOpacity={tooltipOpacity}
                tooltipData={tooltipData}
            />
        </div>
    );
};

export default Scatterplot;